import React, { useEffect, useState } from "react";
import { Input, Modal, Drawer, Spin, message } from "antd";
import { CloseOutlined, SearchOutlined, EyeOutlined, EditOutlined, CloseCircleFilled } from "@ant-design/icons";
import useTransactionStore from "../../../../stores/useTransactionStore";
import useCartStore from "../../../../stores/useCartStore";
import { toCamelCase } from "./customerFormUtils";
import useLookupFirstEnter from "./useLookupFirstEnter";
import { isFashionLayout } from "../../../../utils/fashion/layout";
import { SEARCH_DRAWER_WIDTH, DRAWER_MASK, initialsOf, tierOf, tierTone, searchHint } from "./customerDrawerUi";
import "./customerDrawers.css";

const CustomerSearchDrawer = ({
  // Drawer visibility & close
  displayCustomerSearch,
  closeCustomerSearch,
  // Search input
  customerSearchInputRef,
  customerSearchInput,
  setCustomerSearchInput,
  customerSearchResults,
  setCustomerSearchResults,
  // Customer actions
  selectCustomer,
  showAddNewCustomerFields,
  showEditOldCustomerFields,
  // Display state setters
  setDisplayCustomerSearch,
  setDisplayAddNewCustomer,
  setDisplayEditOldCustomer,
  // Keyboard / layout
  setLayoutName,
  setInputName,
  setOpen,
  keyboard,
  setEditFlag,
  setIsViewMode,
  // Edit customer
  setSelectedEditOldCustomer,
  setActiveTabName,
  // Data
  cart,
  tillData,
  posConfig,
  retailCustomerRules,
  retailCustomerFields,
  // Form helpers
  customerForm,
  convertMetaDataToKeyValue,
  setViewCustomerValues,
  // Auto-enable helpers
  autoEnablePanCustomer,
  autoEnableNriBilling,
  // Stock verification & loading
  verifyStock,
  hasShownCustomerModalRef,
  loading,
  setLoading,
  // Keyboard input handler (defined in parent as onChangeInput)
  onChangeInput,
  // Enter looks the customer up before offering registration. Off unless
  // asked for; the Fashion layout asks for it.
  lookupBeforeAdd,
}) => {
  const selectedReturnType = useTransactionStore.getState().selectedReturnType;
  const { onPressEnter } = useLookupFirstEnter({
    enabled: lookupBeforeAdd ?? isFashionLayout(tillData),
    tillData,
    customerSearchInput,
    customerSearchResults,
    setCustomerSearchResults,
    setLoading,
    selectCustomer,
    showAddNewCustomerFields,
    onLookupFailed: () => message.error("Couldn't look up the customer. Check the connection and try again."),
  });

  const term = (customerSearchInput || "").trim();
  const results = Array.isArray(customerSearchResults) ? customerSearchResults : [];
  const [selectedIdx, setSelectedIdx] = useState(0);
  useEffect(() => setSelectedIdx(0), [customerSearchResults]);
  const selected = term ? results[selectedIdx] || null : null;
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  const canAddNew = selectedReturnType !== "STORE_CREDIT";

  const handleClose = () => {
    const defaultCustomer = cart?.customer?.cwrCustomerId === tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId;
    const isPreOrderPage = window.location.pathname.includes("/preorder");

    // === PAN RULE CHECK ===
    const panRule = retailCustomerRules?.find((r) => r.action === "Request PAN");
    const exchCart = useCartStore.getState().exchangeCart;
    const panActiveCart = selectedReturnType === "EXCHANGE" && exchCart?.items?.length > 0 ? exchCart : cart;
    const isNriBillingCustomer = panActiveCart?.customer?.nriBilling === true;
    const panRuleActive = !isNriBillingCustomer && panRule && Number(panActiveCart?.total) > Number(panRule.value);

    // 1. PAN RULE VIOLATED - TRAP (Close only)
    if (panRuleActive) {
      Modal.confirm({
        title: "Action Required",
        content: "You cannot proceed without selecting a customer.",
        width: 420,
        centered: true,
        closable: false,
        keyboard: false,
        maskClosable: false,
        cancelText: "Close",
        cancelButtonProps: {
          type: "primary",
          size: "middle",
        },
        onCancel: () => {},
      });
      setLayoutName("default");
      return;
    }

    // 2. DEFAULT CUSTOMER + "Before Payment" MODE - SHOW "Yes, Proceed"
    //    BUT when customerRequired="Y", never show the proceed option.
    if (defaultCustomer && navigator.onLine && posConfig?.showCustomerSearchValue === "Before Payment") {
      if (isPreOrderPage || posConfig?.customerRequired === "Y") {
        Modal.confirm({
          title: "Customer not selected",
          content:
            posConfig?.customerRequired === "Y" ? "Customer is required to complete this order. Please add a customer." : "Would you like to proceed for payment without adding Customer?",
          width: 420,
          centered: true,
          okButtonProps: { style: { display: "none" } },
          cancelText: posConfig?.customerRequired === "Y" ? "OK" : "Cancel",
          onCancel: () => {},
        });
      } else {
        Modal.confirm({
          title: "Customer not selected",
          content: "Would you like to proceed for payment without adding Customer?",
          width: 420,
          centered: true,
          okText: "Yes, Proceed",
          cancelText: "Cancel",
          onOk: () => {
            closeCustomerSearch();
            verifyStock();
          },
          onCancel: () => {},
        });
      }
    } else {
      closeCustomerSearch();
    }

    setLayoutName("default");
  };

  // View (read-only) or edit an existing customer from a result row.
  const openCustomerDetails = async (customerSearchResult, editMode) => {
    if (retailCustomerFields && retailCustomerFields.length > 0) {
      const mappedCustomer = convertMetaDataToKeyValue(customerSearchResult);
      const loyaltyBalanceField = retailCustomerFields.find((f) => f.name?.toLowerCase().includes("loyalty balance"));
      const loyaltyBalance = toCamelCase(loyaltyBalanceField?.name);
      await customerForm.setFieldsValue({
        ...mappedCustomer,
        mobileno: mappedCustomer?.mobileNo,
        lastname: mappedCustomer?.lastName,
        lastvisitdate: mappedCustomer?.lastVisitDate,
        cardnumber: mappedCustomer?.cardNumber,
        city: mappedCustomer?.customerAddress?.city,
        taxID: mappedCustomer?.taxId,
        anniversaryDate: mappedCustomer?.anniversaryDate?.split(" ")[0],
        birthday: mappedCustomer?.birthday?.split(" ")[0],
        validity: mappedCustomer?.validity?.split(" ")[0],
        loyaltyLevel: mappedCustomer?.loyaltyLevel?.cwrLoyaltyLevelId || null,
        [loyaltyBalance]: mappedCustomer?.retlLoyaltyBalance,
      });
      autoEnablePanCustomer(mappedCustomer);
      autoEnableNriBilling(mappedCustomer);
      setViewCustomerValues(mappedCustomer);
      setSelectedEditOldCustomer(customerSearchResult);
      setDisplayEditOldCustomer(true);
      setDisplayAddNewCustomer(false);
      if (editMode) setEditFlag(true);
      setIsViewMode(!editMode);
    } else if (editMode) {
      showEditOldCustomerFields(customerSearchResult);
      setEditFlag(true);
    } else {
      setEditFlag(false);
      showEditOldCustomerFields(customerSearchResult);
    }
  };

  const startNewCustomer = () => {
    setActiveTabName("contact");
    showAddNewCustomerFields();
  };

  const clearSearch = () => {
    setCustomerSearchInput("");
    setCustomerSearchResults([]);
    setLoading(false);
  };

  const hint = searchHint(term, loading ? null : results.length, { offline });

  return (
    <Drawer
      open={displayCustomerSearch}
      width={SEARCH_DRAWER_WIDTH}
      title={null}
      closeIcon={null}
      rootClassName="cwc"
      styles={{ header: { display: "none" }, mask: DRAWER_MASK }}
      placement="right"
      closable={false}
      maskClosable={false}
      keyboard={false}
    >
      <div className="cwc-col">
        <div className="cwc-head">
          <h2 className="cwc-title">Add customer to bill</h2>
          <button type="button" className="cwc-ib" aria-label="Close" onClick={handleClose}>
            <CloseOutlined />
          </button>
        </div>

        <Input
          ref={customerSearchInputRef}
          placeholder="Name, phone or email"
          id="sm-product-search"
          className="customerSearchInput cwc-in"
          aria-label="Search customers"
          prefix={loading ? <Spin size="small" /> : <SearchOutlined style={{ color: "#6a7483" }} />}
          suffix={
            customerSearchInput ? (
              <button type="button" className="cwc-ib sm remove_btn" aria-label="Clear search" onClick={clearSearch} style={{ marginRight: -8 }}>
                <CloseCircleFilled style={{ color: "#6a7483" }} />
              </button>
            ) : null
          }
          value={customerSearchInput}
          onFocus={() => {
            setTimeout(() => {
              customerSearchInputRef.current?.focus();
              setInputName("customerSearch");
              setOpen(true);
              const currentInputValue = customerSearchInput || "";
              keyboard?.current?.setInput(currentInputValue);
            }, 300);
          }}
          onPressEnter={() => onPressEnter(loading)}
          onChange={(e) => {
            if (navigator.onLine) {
              onChangeInput(e);
              if (e.target.value === "") {
                setCustomerSearchResults([]);
                setCustomerSearchInput(e.target.value);
                setLoading(false);
              } else {
                setCustomerSearchInput(e.target.value);
              }
            } else {
              setCustomerSearchInput(e.target.value);
              setLoading(false);
              message.warning("You are offline. Customer search requires an internet connection.");
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
            } else if (e.key === "ArrowDown" && results.length > 0) {
              e.preventDefault();
              setSelectedIdx((i) => Math.min(i + 1, results.length - 1));
            } else if (e.key === "ArrowUp" && results.length > 0) {
              e.preventDefault();
              setSelectedIdx((i) => Math.max(i - 1, 0));
            }
          }}
        />
        <p className={`cwc-hint${offline ? " warn" : ""}`} aria-live="polite">
          {hint}
        </p>

        {!term ? (
          <div className="cwc-empty">
            <b>Find the customer</b>
            Type a few letters of their name or the last digits of their phone.
          </div>
        ) : results.length > 0 ? (
          <div className="cwc-list" role="listbox" aria-label="Matching customers">
            {results.map((customerSearchResult, index) => {
              const tier = tierOf(customerSearchResult);
              const meta = [customerSearchResult.mobileNo, customerSearchResult.email].filter(Boolean).join(" · ");
              return (
                <div
                  key={`${index}`}
                  role="option"
                  aria-selected={index === selectedIdx}
                  tabIndex={0}
                  className={`cwc-row${index === selectedIdx ? " on" : ""}`}
                  onClick={() => selectCustomer(index)}
                  onFocus={() => setSelectedIdx(index)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      selectCustomer(index);
                    }
                  }}
                >
                  <span className="cwc-av" aria-hidden="true">
                    {initialsOf(customerSearchResult.name)}
                  </span>
                  <span className="cwc-who">
                    <div className="cwc-name">{customerSearchResult.name}</div>
                    {meta && <div className="cwc-meta">{meta}</div>}
                  </span>
                  {tier && (
                    <span className={`cwc-pill ${tierTone(tier)}`}>
                      <i />
                      {tier}
                    </span>
                  )}
                  <button
                    type="button"
                    className="cwc-ib sm"
                    aria-label={`View ${customerSearchResult.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openCustomerDetails(customerSearchResult, false);
                    }}
                  >
                    <EyeOutlined />
                  </button>
                  <button
                    type="button"
                    className="cwc-ib sm"
                    aria-label={`Edit ${customerSearchResult.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openCustomerDetails(customerSearchResult, true);
                    }}
                  >
                    <EditOutlined />
                  </button>
                </div>
              );
            })}
          </div>
        ) : loading ? (
          <div className="cwc-empty">
            <Spin />
          </div>
        ) : (
          <div className="cwc-empty">
            <b>No customer found</b>
            {canAddNew ? "Check the spelling or number, or create a new customer." : "Check the spelling or number and try again."}
          </div>
        )}

        <div className="cwc-foot">
          {canAddNew && (
            <button type="button" className="cwc-bs" style={{ flex: 1 }} onClick={startNewCustomer}>
              New customer
            </button>
          )}
          <button type="button" className="cwc-bp" style={{ flex: 1 }} disabled={!selected} onClick={() => selected && selectCustomer(selectedIdx)}>
            {selected ? `Add ${selected.name}` : "Add to bill"}
          </button>
        </div>
      </div>
    </Drawer>
  );
};

export default CustomerSearchDrawer;
